import { memo, useEffect, useRef, useState, useCallback } from 'react';
import styles from './styles.module.scss';
import { Icon } from '../Icon';
import { EventsManager } from '@game-libs/events-manager';
import { humanNumber } from '@game-libs/exc-libs/src/helpers';
import { GameplayEvents } from '../../types/game-events';
import { GUIActions } from '../../types/game-types';

// Configuration - MUST be defined before components that use them
const MAX_FLOATING_ELEMENTS = 15;
const OFFSET_X = 130;
const OFFSET_Y_DAMAGE = 30;
const OFFSET_Y_GOLD = 30;
const GOLD_VERTICAL_OFFSET = 100; // How much lower gold spawns compared to damage
const ANIMATION_DURATION = 2000; // CSS animation duration in ms
const CLEANUP_BUFFER = 500; // Extra time before forced cleanup

// Fast ID generator (module-level for performance)
let idCounter = 0;
const generateId = () => ++idCounter;

export enum AttackType {
  Regular = 'regular',
  Critical = 'critical',
  Poison = 'poison',
  Lightning = 'lightning',
}

// Pre-computed style classes to avoid switch statements in render
const STYLE_CLASSES: Record<AttackType, string> = {
  [AttackType.Regular]: '',
  [AttackType.Critical]: styles.critical,
  [AttackType.Poison]: styles.poison,
  [AttackType.Lightning]: styles.lightning,
};

// Pre-computed icon IDs
const ICON_IDS: Record<AttackType, string> = {
  [AttackType.Regular]: 'damage',
  [AttackType.Critical]: 'damage',
  [AttackType.Poison]: 'poison',
  [AttackType.Lightning]: 'lightning',
};

interface FloatingItem {
  id: number;
  value: number;
  attackType: AttackType;
  isGold: boolean;
  x: number;
  y: number;
  screenCoordinates?: { x: number; y: number };
  createdAt: number; // Timestamp for fallback cleanup
}

// Memoized floating number component - only re-renders if props change
const FloatingNumber = memo(function FloatingNumber({
  item,
  onAnimationEnd,
}: {
  item: FloatingItem;
  onAnimationEnd: (id: number) => void;
}) {
  const handleAnimationEnd = useCallback(() => {
    onAnimationEnd(item.id);
  }, [item.id, onAnimationEnd]);

  // Gold spawns lower than damage, with screen coordinates for bird clicks
  const style = item.isGold
    ? item.screenCoordinates
      ? {
          top: item.screenCoordinates.y - 70,
          left: item.screenCoordinates.x - 40,
          position: 'fixed' as const,
          '--randomX': `${item.x}px`,
        }
      : {
          top: item.y + GOLD_VERTICAL_OFFSET,
          left: item.x,
          '--randomX': `${item.x}px`,
        }
    : {
        top: item.y,
        left: item.x,
        '--randomX': `${item.x}px`,
      };

  return (
    <div style={style} className={item.isGold ? styles.dropping : styles.floating} onAnimationEnd={handleAnimationEnd}>
      <div className={`${styles.wrapper} ${STYLE_CLASSES[item.attackType]}`}>
        <Icon iconId={item.isGold ? 'gold' : ICON_IDS[item.attackType]} />
        <span>{humanNumber(item.value)}</span>
      </div>
    </div>
  );
});

/**
 * Optimized FloatingNumbersSource
 *
 * Optimizations:
 * 1. Single state array instead of two separate arrays
 * 2. Uses onAnimationEnd instead of setTimeout (no async task explosion)
 * 3. Single useEffect for all event subscriptions
 * 4. Batches rapid updates using requestAnimationFrame
 * 5. Pre-computed lookup tables for styles/icons
 * 6. Memoized child components with stable callbacks
 */
export const FloatingNumbersSource = memo(function FloatingNumbersSource() {
  const [items, setItems] = useState<FloatingItem[]>([]);

  // Batch buffer - accumulates items between frames
  const batchBuffer = useRef<FloatingItem[]>([]);
  const frameRequested = useRef(false);

  // Flush batched items on next animation frame
  const flushBatch = useCallback(() => {
    if (batchBuffer.current.length === 0) {
      frameRequested.current = false;
      return;
    }

    const newItems = batchBuffer.current;
    batchBuffer.current = [];
    frameRequested.current = false;

    setItems((prev) => {
      // Optimize: avoid creating intermediate arrays when possible
      const totalLength = prev.length + newItems.length;

      if (totalLength <= MAX_FLOATING_ELEMENTS) {
        // Simple concat when under limit - still creates new array but only one
        return prev.concat(newItems);
      }

      // When over limit, only keep the newest items
      // Calculate how many old items to keep
      const keepFromPrev = Math.max(0, MAX_FLOATING_ELEMENTS - newItems.length);

      if (keepFromPrev === 0) {
        // All new items, just slice newItems
        return newItems.slice(-MAX_FLOATING_ELEMENTS);
      }

      // Keep some old + all new
      return prev.slice(-keepFromPrev).concat(newItems);
    });
  }, []);

  // Add item to batch buffer, schedule flush
  const addItem = useCallback(
    (value: number, attackType: AttackType, isGold: boolean, screenCoordinates?: { x: number; y: number }) => {
      if (value <= 0) return;

      const yOffset = isGold ? OFFSET_Y_GOLD : OFFSET_Y_DAMAGE;

      const item: FloatingItem = {
        id: generateId(),
        value,
        attackType,
        isGold,
        x: Math.floor(Math.random() * OFFSET_X) - OFFSET_X / 2,
        y: Math.floor(Math.random() * yOffset) - yOffset / 2,
        screenCoordinates,
        createdAt: Date.now(),
      };

      batchBuffer.current.push(item);

      // Request animation frame only once per batch
      if (!frameRequested.current) {
        frameRequested.current = true;
        requestAnimationFrame(flushBatch);
      }
    },
    [flushBatch],
  );

  // Remove item when animation ends
  const removeItem = useCallback((id: number) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  // Fallback cleanup for items where onAnimationEnd didn't fire
  // This prevents stuck items from accumulating
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      const maxAge = ANIMATION_DURATION + CLEANUP_BUFFER;

      setItems((prev) => {
        const filtered = prev.filter((item) => now - item.createdAt < maxAge);
        // Only update if something was removed
        return filtered.length === prev.length ? prev : filtered;
      });
    }, 1000); // Check every second

    return () => clearInterval(cleanupInterval);
  }, []);

  // Single useEffect for ALL event subscriptions
  useEffect(() => {
    const { on } = EventsManager();
    const unsubscribers: (() => void)[] = [];

    // Regular/Critical damage
    unsubscribers.push(
      on(
        GameplayEvents.DAMAGE_DRAGON,
        ({ damage, gold, isCritical }: { damage: number; gold: number; isCritical: boolean }) => {
          const attackType = isCritical ? AttackType.Critical : AttackType.Regular;
          addItem(damage, attackType, false);
          addItem(gold, attackType, true);
        },
      ),
    );

    // Poison damage
    unsubscribers.push(
      on(GameplayEvents.POISON_DAMAGE_DRAGON, ({ damage, gold }: { damage: number; gold: number }) => {
        addItem(damage, AttackType.Poison, false);
        addItem(gold, AttackType.Poison, true);
      }),
    );

    // Lightning damage
    unsubscribers.push(
      on(GameplayEvents.LIGHTNING_DAMAGE_DRAGON, ({ damage, gold }: { damage: number; gold: number }) => {
        addItem(damage, AttackType.Lightning, false);
        addItem(gold, AttackType.Lightning, true);
      }),
    );

    // Bird click
    unsubscribers.push(
      on(
        GUIActions.CLICK_BIRD,
        ({
          moneyToAward,
          screenCoordinates,
        }: {
          moneyToAward: number;
          screenCoordinates?: { x: number; y: number };
        }) => {
          addItem(moneyToAward, AttackType.Regular, true, screenCoordinates);
        },
      ),
    );

    // Cleanup all subscriptions on unmount
    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [addItem]);

  // Early return if nothing to render
  if (items.length === 0) {
    return null;
  }

  return (
    <div className={styles.source}>
      {items.map((item) => (
        <FloatingNumber key={item.id} item={item} onAnimationEnd={removeItem} />
      ))}
    </div>
  );
});
